<template>
  <LabelGroup
    :visible-tags="visible"
    :additional-tags="additional"
    :data-test="`tc-skills-${index}`"
    sm
    gray
    subtle
    tooltip-is-global
  >
    <template #tooltip-header>
      <WdsIcon name="tag" :size="16" />
      {{ $t('Skills') }}
    </template>
  </LabelGroup>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { WdsIcon } from '@worksome/wds-components'
import LabelGroup from '@/components/LabelGroup.vue'
import { AccountType } from '@/gql/graphql'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useTrustedContactsStore } from '@/components/GraphqlDataTable/filters/trustedContacts/trusted-contacts-store'

const TrustedContactSkillsColumn = graphql(`
  fragment TrustedContactSkillsColumn on TrustedContact {
    id
    skills {
      id
      name
    }
    worker {
      id
      skills {
        id
        name
      }
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof TrustedContactSkillsColumn>
  index: number
}>()

const { showOnlyCompanySkills } = storeToRefs(useTrustedContactsStore())

const column = computed(() => useFragment(TrustedContactSkillsColumn, props.row))
const max = 3

const companySkills = computed(() =>
  column.value.skills.map((s) => ({ ...s, skillType: AccountType.Company })),
)

const combinedSkills = computed(() => {
  const map = new Map<string, { id: string; name: string; skillType?: AccountType }>()
  column.value.skills.forEach((s) => map.set(s.id, { ...s, skillType: AccountType.Company }))
  column.value.worker.skills.forEach((s) => {
    if (!map.has(s.id)) map.set(s.id, { ...s })
  })
  return Array.from(map.values())
})

const displaySkills = computed(() =>
  showOnlyCompanySkills.value ? companySkills.value : combinedSkills.value,
)

const visible = computed(() =>
  displaySkills.value
    .slice(0, max)
    .map((s: any) => ({ name: s.name, primary: s.skillType === AccountType.Company })),
)
const additional = computed(() => displaySkills.value.slice(max).map((s) => s.name))
</script>

<style scoped></style>
